import { useEffect, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { Grid, List } from 'lucide-react'
import useRequest from '@/shared/lib/use-request'
import { AppDetailStore, ConfigStore, LangStore } from '@/shared/model/store'
import { UserStore } from '@/entities/user'
import { ContentStore } from '@/entities/feed-block/model/content-store'
import { ContentType } from '@/entities/feed-block/model/types'
import { FeedPageType } from '@/pages/feed-page'
import { cn } from '@/shared/lib'
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { NoContent } from '@/entities/feed-block/ui/no-content'
import BlogUnit from '@/shared/ui/skeletons/ui/blog-unit'
import { useSubscribe } from '@/shared/lib/use-subscribe'
import { SocketStore } from '@/shared/model/store/socket-store'
import FeedBlockLine from '@/entities/feed-block/ui/feed-block-line'
import FeedBlockGrid from '@/entities/feed-block/ui/feed-block-grid'
import NewPostButton from './new-post-button'

type FeedProps = FeedPageType & {
	author_addr?: `0x${string}`
}

type TimeFilter = {
	id: number
	name: string
	type: string
	min_time: number
	max_time: number
}

const LIMIT = 200

// Вынес функцию получения параметров в отдельную утилиту
const getFeedParams = (pageName: FeedPageType['pageType'], user?: `0x${string}`) => {
	switch (pageName) {
		case 'leaders':
			return { order_by: 'total_author_share' }
		case 'actual':
			return { order_by: 'fund_amount' }
		case 'for-you':
			return user ? { user_addr: user } : {}
		default:
			return {}
	}
}

// Вынес функцию расчета дат в отдельную утилиту
const calculateTimeRange = (filter: TimeFilter) => {
	if (!filter.id) return {}
	const now = Date.now()
	const calculated = now - filter.max_time * 1000
	return {
		min_time: new Date(calculated).toISOString().slice(0, 10)
	}
}

type FilterTypes = {
	domain: string
	content_type: 'post'
	limit: number
	author_addr?: `0x${string}`
	user_addr?: `0x${string}`
	offset?: number
	order_by?: string
	min_time?: string
	max_time?: string
	category?: string
	show_nsfw?: boolean
}

export const Feed = ({ pageType, author_addr }: FeedProps) => {
	const { appDetails } = AppDetailStore()
	const { langPack, lang } = LangStore()
	const { userData } = UserStore()
	const location = useLocation()
	const { setContentList, contenList } = ContentStore()
	const { domain } = ConfigStore()
	const { fetch, isLoading, isSuccess, result } = useRequest<{ list: ContentType[] }>('getContentList')
	const { dropMessages } = SocketStore()
	const { message: newContentMessage } = useSubscribe('content_processed')

	// Подписки на аукционы
	const auctionSubscriptions = {
		created: useSubscribe('nft_auction_created').message,
		canceled: useSubscribe('nft_auction_canceled').message,
		ended: useSubscribe('nft_auction_ended').message,
		bid: useSubscribe('nft_auction_bid').message
	}

	const [newPosts, setNewPosts] = useState<string[]>([])
	const [viewMode, setViewMode] = useState<'list' | 'grid'>(
		() => (localStorage.getItem('lt') as 'list' | 'grid') || 'list'
	)

	const DEFAULT_PARAMS: FilterTypes = {
		domain,
		content_type: 'post',
		limit: LIMIT,
		show_nsfw: false,
		...(author_addr && { author_addr }),
		...(userData?.address && {
			my_addr: userData.address,
			show_nsfw: ['w', 's'].includes(userData.nsfw)
		})
	}

	const [fetchParams, setFetchParams] = useState<FilterTypes>({ ...DEFAULT_PARAMS, ...getFeedParams(pageType) })
	const [selectedCategory, setSelectedCategory] = useState(langPack('feed.all_rubrics'))

	// Получение категорий
	const categories = [langPack('feed.all_rubrics'), ...(appDetails?.modules.categories.locales[lang] || [])]

	// Фильтры по времени
	const timeFilters: TimeFilter[] = [
		{ id: 1, name: langPack('feed.week'), type: '7d', min_time: 0, max_time: 604800 },
		{ id: 2, name: langPack('feed.month'), type: '1m', min_time: 0, max_time: 2419200 },
		{ id: 3, name: langPack('feed.year'), type: '1y', min_time: 0, max_time: 31536000 },
		{ id: 4, name: langPack('feed.all_time'), type: 'all', min_time: 0, max_time: 0 }
	]

	// Обработчик изменения периода
	const handlePeriodChange = useCallback(
		(periodType: string) => {
			const selectedFilter = timeFilters.find(filter => filter.type === periodType)

			if (!selectedFilter) return

			setFetchParams(prev => ({
				...prev,
				offset: 0,
				...getFeedParams(pageType),
				...(selectedFilter.max_time !== 0 ? calculateTimeRange(selectedFilter) : {})
			}))
		},
		[pageType]
	)

	// Обработчик изменения категории
	const handleCategoryChange = useCallback(
		(category: string) => {
			setSelectedCategory(category)

			setFetchParams(prev => {
				const newParams = { ...prev }
				if (category !== langPack('feed.all_rubrics')) {
					newParams.category = `${lang}:${category}`
				} else {
					delete newParams.category
				}
				return newParams
			})
		},
		[lang]
	)

	// Загрузка данных при изменении параметров
	useEffect(() => {
		if (!Object.keys(fetchParams).length) return
		console.log('fetchParams', fetchParams)
		const loadData = async () => {
			await fetch({
				params: fetchParams,
				onSuccess: res => {
					console.log('res', res)
					setContentList(res.list)
				}
			})
		}

		loadData()
	}, [fetchParams])

	// Обработчик изменения режима просмотра
	const handleViewModeChange = (mode: string) => {
		if (mode === 'list' || mode === 'grid') {
			setViewMode(mode)
			localStorage.setItem('lt', mode)
		}
	}

	// Сброс состояния при смене типа страницы
	useEffect(() => {
		setSelectedCategory(langPack('feed.all_rubrics'))
		window.scrollTo({ top: 0, behavior: 'smooth' })

		setFetchParams({
			...DEFAULT_PARAMS,
			...(pageType === 'leaders' && {
				...calculateTimeRange(timeFilters[1]),
				...getFeedParams(pageType)
			}),
			...getFeedParams(pageType, userData?.address)
		})
	}, [pageType])

	// Обработка новых сообщений для страницы "new"
	useEffect(() => {
		if (
			!newContentMessage ||
			pageType !== 'new' ||
			newContentMessage?.content?.savva_content?.content_type !== 'post' ||
			newContentMessage?.edited
		)
			return

		const postId = newContentMessage.content.savva_cid
		const isNewPost = newPosts.includes(postId)
		const isFromNewPage = !!location.state?.from_new
		const postCategories = newContentMessage.content.savva_content.locales[lang]?.categories || []
		const isInSelectedCategory =
			selectedCategory === langPack('feed.all_rubrics') || postCategories.includes(selectedCategory)

		if (isNewPost || isFromNewPage || !isInSelectedCategory) return

		setNewPosts(prev => [...prev, postId])
		location.state = {} // Очищаем состояние после обработки
	}, [newContentMessage, pageType, newPosts, location, lang, selectedCategory])

	// Обработчики аукционов
	useEffect(() => {
		const updateAuctionStatus = (contentId: string, updates: Partial<ContentType['nft']>) => {
			setContentList(prev =>
				prev.map(item => (item.savva_cid === contentId ? { ...item, nft: { ...item.nft, ...updates } } : item))
			)
		}

		if (auctionSubscriptions.created) {
			const { content_id, min_price, end_time } = auctionSubscriptions.created
			updateAuctionStatus(content_id, {
				on_auction: true,
				min_bid: min_price,
				auction_end_time: end_time
			})
		}

		if (auctionSubscriptions.canceled || auctionSubscriptions.ended) {
			const contentId = auctionSubscriptions.canceled?.content_id || auctionSubscriptions.ended?.content_id
			updateAuctionStatus(contentId, { on_auction: false })
		}

		if (auctionSubscriptions.bid) {
			const { content_id, price } = auctionSubscriptions.bid
			updateAuctionStatus(content_id, { highest_bid: price })
		}
	}, [auctionSubscriptions, setContentList])

	// Сброс при размонтировании
	useEffect(
		() => () => {
			setContentList([])
		},
		[]
	)

	// Обработчик новых постов
	const handleNewPosts = useCallback(() => {
		setNewPosts([])
		dropMessages('content_processed')
		setFetchParams(prev => ({ ...prev })) // Триггер обновления
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}, [])

	return (
		<div className='relative'>
			<div className='row flex gap-2'>
				{/* Переключатель режима просмотра */}
				<ToggleGroup value={viewMode} type='single' className='hidden lg:flex' onValueChange={handleViewModeChange}>
					<ToggleGroupItem value='list' aria-label='List view'>
						<List />
					</ToggleGroupItem>
					<ToggleGroupItem value='grid' aria-label='Grid view'>
						<Grid />
					</ToggleGroupItem>
				</ToggleGroup>

				{/* Селектор категорий */}
				<Select value={selectedCategory} onValueChange={handleCategoryChange}>
					<SelectTrigger>
						<SelectValue placeholder={langPack('feed.select_category')} />
					</SelectTrigger>
					<SelectContent>
						<SelectGroup>
							{categories.map(category => (
								<SelectItem key={category} value={category}>
									{category}
								</SelectItem>
							))}
						</SelectGroup>
					</SelectContent>
				</Select>

				{/* Фильтр по времени (только для leaders) */}
				{pageType === 'leaders' && (
					<Select defaultValue={timeFilters[1].type} onValueChange={handlePeriodChange}>
						<SelectTrigger>
							<SelectValue placeholder={langPack('feed.select_period')} />
						</SelectTrigger>
						<SelectContent>
							<SelectGroup>
								{timeFilters.map(filter => (
									<SelectItem key={filter.id} value={filter.type}>
										{filter.name}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
				)}
			</div>

			{/* Лоадер */}
			{isLoading && (
				<div
					className={cn(
						viewMode === 'grid' ? 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-4'
					)}
				>
					{Array.from({ length: 12 }).map((_, index) => (
						<BlogUnit key={index} isGrid={viewMode === 'grid'} />
					))}
				</div>
			)}

			{/* Кнопка новых постов */}
			{pageType === 'new' && newPosts.length > 0 && <NewPostButton onClick={handleNewPosts} newPosts={newPosts} />}

			{/* Контент */}
			{isSuccess ? (
				contenList.length > 0 ? (
					<div
						className={cn(
							viewMode === 'grid' ? 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3' : 'flex flex-col gap-4'
						)}
					>
						{contenList.map(content =>
							viewMode === 'grid' ? (
								<FeedBlockGrid key={content.savva_cid} content={content} />
							) : (
								<FeedBlockLine key={content.savva_cid} content={content} />
							)
						)}
					</div>
				) : (
					<NoContent isLoading={isLoading} />
				)
			) : null}
		</div>
	)
}
